import type { Ref } from 'vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { onMounted, onUnmounted, ref } from 'vue';
import { isCancel } from 'axios';

import { fetchGetUserPrinterOptions } from '@/helpers/fetchers';

export interface PrintDialogDataComposable {
  isFetchingUserPrinters: Ref<boolean>;
  fetchedUserPrinters: Ref<
    openapi.UserPrinterOptionsContentOpenapi | undefined
  >;
  userPrinters: Ref<openapi.UserPrinterOptionsContentOpenapi | undefined>;
}

export default function usePrintDialogData(): PrintDialogDataComposable {
  const isFetchingUserPrinters = ref(false);
  const fetchedUserPrinters = ref<openapi.UserPrinterOptionsContentOpenapi>();
  const userPrinters = ref<openapi.UserPrinterOptionsContentOpenapi>();

  let userPrintersController: AbortController | undefined;

  async function _fetchUserPrinters(): Promise<void> {
    fetchedUserPrinters.value = undefined;
    if (userPrintersController) userPrintersController.abort();
    isFetchingUserPrinters.value = true;
    userPrintersController = new AbortController();
    await fetchGetUserPrinterOptions({
      signal: userPrintersController.signal,
    })
      .then(function (data) {
        fetchedUserPrinters.value = data;
      })
      .catch((err: unknown) => {
        if (!isCancel(err)) throw err;
      })
      .finally(function () {
        isFetchingUserPrinters.value = false;
      });
  }

  function clear(): void {
    if (userPrintersController) userPrintersController.abort();
  }

  onUnmounted(function () {
    clear();
  });

  onMounted(async function () {
    await _fetchUserPrinters();
    userPrinters.value = fetchedUserPrinters.value;
  });

  return {
    isFetchingUserPrinters,
    fetchedUserPrinters,
    userPrinters,
  };
}
